/**
 * User Data Storage Service
 *
 * Handles storing and retrieving both Profile User and Anonymous User data
 * from localStorage. All functions return Promises to mimic asynchronous API behavior.
 *
 * This service provides a centralized interface for all localStorage operations,
 * with consistent error handling, data validation, and async patterns.
 */

// Storage keys - single source of truth for each data type
const PROFILE_USER_KEY = 'profile_user_data'; // Stores the profile user's data
const ANON_USER_KEY = 'anon_user_data'; // Stores the anonymous user's data (legacy global key)
const ANON_USER_DATA_PREFIX = 'anon_user_data_'; // Prefix for chatRoomId-specific anonymous user data
const ANON_CONTACT_DATA_KEY = 'anonContactData'; // Stores anonymous contact information
const LOCATION_PERMISSION_KEY = 'location_permission'; // Location permission status
const CURRENT_SCREEN_KEY = 'current_screen'; // Current screen for navigation
const SELECTED_CONTACT_TYPE_KEY = 'selected_contact_type'; // Selected contact type
const MESSAGE_QUEUE_KEY = 'message_queue'; // Queued messages for reliable delivery
const APP_VERSION_KEY = 'appVersion'; // Current app version

// Key prefixes for connection-specific data
export const CONNECTION_METADATA_PREFIX = 'connection_metadata_';
export const RECEIVED_ANON_DATA_PREFIX = 'received_anon_data_';
export const RECEIVED_PROFILE_DATA_PREFIX = 'received_profile_data_';
export const CONNECTION_GROUP_PREFIX = 'connection_group_'; // Legacy — cleared during cleanup
export const CONNECTION_TAGS_PREFIX = 'connection_tags_'; // Legacy — cleared during cleanup
export const MANUAL_CONTACT_DATA_PREFIX = 'manual_contact_data_';
const LAST_SELECTED_GROUP_KEY = 'last_selected_connection_group'; // Legacy - no longer used

// Key prefixes for notes
export const PROFILE_NOTES_PREFIX = 'profile_notes_';
export const ANON_NOTES_PREFIX = 'anon_notes_';

// Key prefix for chat messages
export const CHAT_MESSAGES_PREFIX = 'chat_messages_';

// Key prefix for last read timestamp (for unread indicators)
export const LAST_READ_PREFIX = 'last_read_';

/**
 * Generic function to get an item from localStorage
 * @param {string} key - The key to retrieve
 * @returns {Promise<any>} The parsed value or null if not found
 */
export const getItem = async (key) => {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch {
      // If it's not valid JSON, return as is (string)
      return data;
    }
  } catch (error) {
    console.error(`Error getting item [${key}]:`, error);
    return null;
  }
};

/**
 * Generic function to set an item in localStorage
 * @param {string} key - The key to set
 * @param {any} value - The value to store
 * @returns {Promise<boolean>} Success status
 */
export const setItem = async (key, value) => {
  try {
    if (value === undefined) {
      console.error(`Cannot store undefined value for key [${key}]`);
      return false;
    }

    const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, valueToStore);
    return true;
  } catch (error) {
    console.error(`Error setting item [${key}]:`, error);
    return false;
  }
};

/**
 * Generic function to remove an item from localStorage
 * @param {string} key - The key to remove
 * @returns {Promise<boolean>} Success status
 */
export const removeItem = async (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing item [${key}]:`, error);
    return false;
  }
};

/**
 * Get profile data from localStorage
 * @returns {Promise<Object|null>} Profile data or null if not found
 */
export const getProfileData = async () => {
  try {
    return await getItem(PROFILE_USER_KEY);
  } catch (error) {
    console.error('[STORAGE] Error getting profile data:', error);
    return null;
  }
};

/**
 * Get notes for a chat room
 * @param {string} chatRoomId - Unique identifier for the chat room
 * @param {boolean} isProfileUser - Whether the notes are for a profile user or anonymous user
 * @returns {Promise<string>} Notes for the chat room or empty string if not found
 */
export const getNotes = async (chatRoomId, isProfileUser = true) => {
  try {
    if (!chatRoomId) return '';

    // Use the appropriate key based on user type
    const noteKey = isProfileUser
      ? `${PROFILE_NOTES_PREFIX}${chatRoomId}`
      : `${ANON_NOTES_PREFIX}${chatRoomId}`;

    const notes = await getItem(noteKey);
    return notes || '';
  } catch (error) {
    console.error('[STORAGE] Error getting notes:', error);
    return '';
  }
};

/**
 * Save notes for a chat room
 * @param {string} chatRoomId - Unique identifier for the chat room
 * @param {string} notes - Notes to save for the chat room
 * @param {boolean} isProfileUser - Whether the notes are for a profile user or anonymous user
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const saveNotes = async (chatRoomId, notes, isProfileUser = true) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for saving notes');
      return false;
    }

    // Use the appropriate key based on user type
    const noteKey = isProfileUser
      ? `${PROFILE_NOTES_PREFIX}${chatRoomId}`
      : `${ANON_NOTES_PREFIX}${chatRoomId}`;

    // Save the notes
    const result = await setItem(noteKey, notes || '');

    // Dual-write to Supabase connection_data (Profile users only)
    if (isProfileUser) {
      upsertConnectionData(chatRoomId, { notes: notes || '' });
    }

    // Dual-write to connection_views (both profile and anon users)
    const userId = isProfileUser ? getProfileUserId() : localStorage.getItem('anon_user_id');
    if (userId) {
      upsertConnectionView(chatRoomId, userId, { notes: notes || '' });
    }

    return result;
  } catch (error) {
    console.error('[STORAGE] Error saving notes:', error);
    return false;
  }
};

/**
 * Get chat messages for a specific chat room
 * @param {string} chatRoomId - Unique identifier for the chat room
 * @returns {Promise<Array>} Array of chat messages, or empty array if not found
 */
export const getChatMessages = async (chatRoomId) => {
  try {
    if (!chatRoomId) return [];

    const key = `${CHAT_MESSAGES_PREFIX}${chatRoomId}`;
    const messages = await getItem(key);
    return messages || [];
  } catch (error) {
    console.error('[STORAGE] Error getting chat messages:', error);
    return [];
  }
};

/**
 * Save chat messages for a specific chat room
 * @param {string} chatRoomId - Unique identifier for the chat room
 * @param {Array} messages - Array of chat message objects to save
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const saveChatMessages = async (chatRoomId, messages) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for saving chat messages');
      return false;
    }

    if (!Array.isArray(messages)) {
      console.error('[STORAGE] Chat messages must be an array');
      return false;
    }

    // Filter out in-flight messages — blob URLs must never reach localStorage
    const persistable = messages.filter((m) => !m.uploading && !m.uploadFailed);
    const key = `${CHAT_MESSAGES_PREFIX}${chatRoomId}`;
    return await setItem(key, persistable);
  } catch (error) {
    console.error('[STORAGE] Error saving chat messages:', error);
    return false;
  }
};

/**
 * Save profile data to localStorage
 * @param {Object} profileData - Profile data to save
 * @returns {Promise<boolean>} Success status
 */
export const saveProfileData = async (
  profileData,
  { ownPhotoMeta = false, ownColour = false } = {}
) => {
  try {
    if (!profileData) {
      console.error('[STORAGE] No profile data provided to save');
      return false;
    }

    // photoMeta is written by its own capture path (services/photo) —
    // saveOwnPhotoMeta, the only caller passing ownPhotoMeta — often while a
    // screen holds an older copy of the profile in form state. That copy can
    // neither delete the stored meta (omission) nor overwrite it with a stale
    // one (keepPhotoMeta) — the same rule syncProfileToSupabase applies DB-side.
    // Only where capture can run — on main no profile carries photoMeta, so
    // the extra read would be wasted on every save.
    // The chosen colour follows the same rule: only saveOwnColour (ownColour)
    // writes it, so a stale form copy can't put back a colour the person has
    // since changed (refactor row 111).
    if ((!ownPhotoMeta || !ownColour) && isNewExperience()) {
      const stored = await getItem(PROFILE_USER_KEY);
      // Same person only — a different account's save never inherits it.
      const samePerson = stored?.profileUserId === profileData.profileUserId;
      if (!ownPhotoMeta && samePerson && stored?.photoMeta) {
        const photoMeta = keepPhotoMeta(
          profileData.photoMeta,
          stored.photoMeta,
          profileData.photoUrl
        );
        profileData = { ...profileData, photoMeta };
      }
      if (!ownColour && samePerson && stored?.colour) {
        profileData = { ...profileData, colour: stored.colour };
      }
    }

    return await setItem(PROFILE_USER_KEY, profileData);
  } catch (error) {
    console.error('[STORAGE] Error saving profile data:', error);
    return false;
  }
};

/**
 * Store a freshly captured photo meta on the own profile (signup-foundations)
 * — the saveGreetingText recipe below: fresh read, local save, partial
 * non-destructive sync. Skipped without a named profile (first-time signup
 * before the profile exists — accepted: centre crop). Returns true when
 * synced.
 */
export const saveOwnPhotoMeta = async (photoMeta) => {
  const current = await getProfileData();
  if (!current?.name) return false;
  await saveProfileData({ ...current, photoMeta }, { ownPhotoMeta: true });
  const synced = await syncProfileToSupabase(
    {
      profileUserId: current.profileUserId,
      name: current.name,
      photoMeta,
    },
    { ownPhotoMeta: true }
  );
  return synced === true;
};

/**
 * Store the person's chosen colour (signup A′, Drop 7 SYSTEM §8) — one of the
 * seven keys (people/personColour), shown to everyone who holds them. Same
 * recipe as saveOwnPhotoMeta: fresh read (a caller's copy may be stale), local
 * save, partial non-destructive sync — the refresh RPC then carries it to every
 * holder. Rejects anything outside the seven; skipped without a named profile.
 * Returns true when synced.
 *
 * @param {string} colour - a PERSON_COLOURS key
 * @returns {Promise<boolean>}
 */
export const saveOwnColour = async (colour) => {
  if (!isPersonColour(colour)) {
    console.error('[STORAGE] saveOwnColour: not one of the seven:', colour);
    return false;
  }
  const current = await getProfileData();
  if (!current?.name) return false;
  await saveProfileData({ ...current, colour }, { ownColour: true });
  const synced = await syncProfileToSupabase(
    {
      profileUserId: current.profileUserId,
      name: current.name,
      colour,
    },
    { ownColour: true }
  );
  return synced === true;
};

// The own-profile fields the new Profile page edits in place (signup D1).
// Anything else (photoMeta, colour, sharedFields, ids) has its own writer;
// photoUrl is here so a new photo or Remove saves like any field (its meta
// follows from the capture pass, guarded by keepPhotoMeta).
const OWN_PROFILE_FIELDS = new Set([
  'name',
  'photoUrl',
  ...ALL_CONTACT_KEYS,
  'otherLabel',
  'currentCity',
  'currentCityPlace',
  'role',
  'organization',
  'whatIDo',
  'placesLived',
]);

/**
 * Save named fields of the own profile (signup D1 — the Profile page commits
 * one field per return). The saveOwnColour recipe for named fields: fresh read
 * (the page's copy may be stale), partial non-destructive sync, local save.
 * Sync goes first because it is the phone-uniqueness gate: on `phone_taken`
 * nothing is saved anywhere. A failed sync (offline) still saves locally, as
 * the old Profile form did. '' clears a field; the name can't be cleared.
 *
 * @param {object} patch - { field: value } — only OWN_PROFILE_FIELDS keys
 * @returns {Promise<{ synced: boolean, error?: string, profile?: object }>}
 */
export const saveOwnProfileFields = async (patch) => {
  const keys = Object.keys(patch || {});
  const invalid = keys.filter((k) => !OWN_PROFILE_FIELDS.has(k));
  if (keys.length === 0 || invalid.length > 0) {
    console.error('[STORAGE] saveOwnProfileFields: not an editable field:', invalid);
    return { synced: false, error: 'invalid_field' };
  }
  if ('name' in patch && !String(patch.name || '').trim()) {
    return { synced: false, error: 'name_required' };
  }
  const current = await getProfileData();
  if (!current?.name) return { synced: false, error: 'no_profile' };

  const synced = await syncProfileToSupabase({
    profileUserId: current.profileUserId,
    name: current.name,
    ...patch,
  });
  if (synced?.error) return { synced: false, error: synced.error };

  const profile = { ...current, ...patch };
  await saveProfileData(profile);
  // D2 live sharing: a cleared contact slot drops its sharing in both tables so
  // a re-added detail starts unshared (rule 11). After the save so a failed
  // sync (above) never clears sharing for a value that's still there. No-op
  // unless NEW_EXPERIENCE. (connectionSharing)
  for (const itemKey of clearedShareItemsFromPatch(patch)) {
    await applyContactSlotChange({ deletedKey: itemKey });
  }
  return { synced: synced === true, profile };
};

/**
 * Persist the saved greeting (exchange-clarity D14) — data-layer home for the
 * read-modify-write + sync recipe (portability contract: this survives to
 * native; the component that calls it doesn't). Fresh read on purpose: the
 * caller's in-memory profile may be stale. Only ever called with non-empty
 * text — an explicit '' would CLEAR the saved value in the sync merge, and
 * D14's per-send clearing must never do that. Returns the updated profile
 * object for the caller's local state, or null when there's no named profile.
 */
export const saveGreetingText = async (text) => {
  if (!text) return null;
  const current = await getProfileData();
  if (!current?.name) return null;
  const updated = { ...current, greetingText: text };
  await saveProfileData(updated);
  syncProfileToSupabase({
    profileUserId: updated.profileUserId,
    name: updated.name,
    greetingText: text,
  });
  return updated;
};

/**
 * Get anonymous data from localStorage
 * @param {string} [chatRoomId] - Optional chat room ID for specific connection
 * @returns {Promise<Object|null>} Anonymous data or null if not found
 */
export const getAnonData = async (chatRoomId) => {
  try {
    // If chatRoomId is provided, use room-specific storage
    if (chatRoomId) {
      const dataKey = `${ANON_USER_DATA_PREFIX}${chatRoomId}`;
      const data = await getItem(dataKey);

      // NO FALLBACKS - If no room-specific data, return null
      // This makes it clear when data is missing rather than serving wrong data
      return data;
    } else {
      // Legacy support for global key
      return await getItem(ANON_USER_KEY);
    }
  } catch (error) {
    console.error('[STORAGE] Error getting anonymous data:', error);
    return null;
  }
};

/**
 * Save anonymous data to localStorage
 * @param {Object} anonData - Anonymous data to save
 * @param {string} [chatRoomId] - Optional chat room ID for specific connection
 * @returns {Promise<boolean>} Success status
 */
export const saveAnonData = async (anonData, chatRoomId) => {
  try {
    if (!anonData) {
      console.error('[STORAGE] No anonymous data provided to save');
      return false;
    }

    // Always save to the global key for backward compatibility
    await setItem(ANON_USER_KEY, anonData);

    // If chatRoomId is provided, also save to room-specific storage
    if (chatRoomId) {
      const dataKey = `${ANON_USER_DATA_PREFIX}${chatRoomId}`;
      await setItem(dataKey, anonData);
    }

    return true;
  } catch (error) {
    console.error('[STORAGE] Error saving anonymous data:', error);
    return false;
  }
};

/**
 * Get data received from a profile user (for anonymous users)
 * @param {string} chatRoomId - Chat room ID to get data for a specific connection
 * @returns {Promise<Object|null>} Profile data received from scanning or null if not found
 */
export const getReceivedProfileData = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('No chat room ID provided for getting received profile data');
      return null;
    }

    const receivedProfileKey = `${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`;
    return await getItem(receivedProfileKey);
  } catch (error) {
    console.error('Error getting received profile data:', error);
    return null;
  }
};

/**
 * Save data received from a profile user (for anonymous users)
 * @param {string} chatRoomId - Chat room ID to save data for a specific connection
 * @param {Object} receivedProfileData - Profile data received from scanning
 * @returns {Promise<boolean>} Success status
 */
export const saveReceivedProfileData = async (chatRoomId, receivedProfileData) => {
  try {
    // Validate we have valid profile data with a proper name
    if (!receivedProfileData) {
      console.error('No profile data provided to save');
      return false;
    }

    if (!chatRoomId) {
      console.error('No chat room ID provided for saving received profile data');
      return false;
    }

    // Strict validation: we must have a proper name string
    if (
      !receivedProfileData.name ||
      typeof receivedProfileData.name !== 'string' ||
      receivedProfileData.name.trim() === ''
    ) {
      console.error('Invalid profile data: missing or empty name field');
      return false;
    }

    console.log('PROFILE_DATA_DEBUG: saveReceivedProfileData saving valid data:', {
      name: receivedProfileData.name,
      photoUrl: receivedProfileData.photoUrl ? 'exists' : 'missing',
      phone: receivedProfileData.phone ? 'exists' : 'missing',
      email: receivedProfileData.email ? 'exists' : 'missing',
      instagram: receivedProfileData.instagram ? 'exists' : 'missing',
      chatRoomId,
    });

    // Save with room-specific key
    const receivedProfileKey = `${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`;
    await setItem(receivedProfileKey, receivedProfileData);

    // Extract meeting metadata if available
    if (receivedProfileData.location || receivedProfileData.date) {
      const metadataKey = `${CONNECTION_METADATA_PREFIX}${chatRoomId}`;

      // Get existing metadata or create new
      let connectionMetadata = {};
      const existingMetadata = await getItem(metadataKey);
      if (existingMetadata) {
        connectionMetadata = existingMetadata;
      }

      // Update with meeting details
      connectionMetadata = {
        ...connectionMetadata,
        location: receivedProfileData.location || connectionMetadata.location,
        meetingDate:
          receivedProfileData.date ||
          receivedProfileData.timestamp ||
          connectionMetadata.meetingDate,
        meetingTime: receivedProfileData.time || connectionMetadata.meetingTime,
        lastUpdated: new Date().toISOString(),
      };

      // Save metadata
      await setItem(metadataKey, connectionMetadata);
    }

    return true;
  } catch (error) {
    console.error('Error saving received profile data:', error);
    return false;
  }
};

/**
 * Get connection metadata
 * @param {string} chatRoomId - Chat room ID for the connection
 * @returns {Promise<Object|null>} Connection metadata or null if not found
 */
export const getConnectionMetadata = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('No chat room ID provided for getting connection metadata');
      return null;
    }

    const metadataKey = `${CONNECTION_METADATA_PREFIX}${chatRoomId}`;
    return await getItem(metadataKey);
  } catch (error) {
    console.error('Error getting connection metadata:', error);
    return null;
  }
};

/**
 * Save connection metadata
 * @param {string} chatRoomId - Chat room ID for the connection
 * @param {Object} connectionMetadata - Metadata to save for the connection
 * @returns {Promise<boolean>} Success status
 */
export const saveConnectionMetadata = async (chatRoomId, connectionMetadata) => {
  try {
    if (!chatRoomId) {
      console.error('No chat room ID provided for saving connection metadata');
      return false;
    }

    if (!connectionMetadata) {
      console.error('No metadata provided to save');
      return false;
    }

    const metadataKey = `${CONNECTION_METADATA_PREFIX}${chatRoomId}`;
    const metadataWithTimestamp = {
      ...connectionMetadata,
      lastUpdated: new Date().toISOString(),
    };
    const result = await setItem(metadataKey, metadataWithTimestamp);

    // Dual-write to Supabase
    upsertConnectionData(chatRoomId, { metadata: metadataWithTimestamp });

    console.log(`Saved connection metadata for room ${chatRoomId}:`, connectionMetadata);
    return result;
  } catch (error) {
    console.error('Error saving connection metadata:', error);
    return false;
  }
};

/**
 * Save received anonymous user data for a specific connection
 * @param {string} chatRoomId - Chat room ID for the connection
 * @param {Object} receivedAnonData - Anonymous user data to save
 * @returns {Promise<boolean>} Success status
 */
export const saveReceivedAnonData = async (chatRoomId, receivedAnonData) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] Missing chatRoomId for saving received anonymous data');
      return false;
    }

    if (!receivedAnonData) {
      console.error('[STORAGE] Missing receivedAnonData to save');
      return false;
    }

    const dataKey = `${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`;

    // Tripwire (device pass 3, B25): the People-card city flickers on/off —
    // every code-visible writer looks merge-safe, so log any write that DROPS
    // a previously-stored city value or its share flag; the caller's stack
    // makes the next occurrence queryable ground truth (the B6 pattern).
    // Skipped only when the D2 READ path is on (off until the backfill): then a
    // city drop is a legitimate resolver wholesale-replace. While the read path is
    // off, the tripwire fires as pre-D2 (a drop is still suspicious).
    if (!isLiveSharingReadOn()) {
      try {
        const existing = await getItem(dataKey);
        const hadCity = !!existing?.currentCity;
        const hadFlag = !!existing?.sharedFields?.includes('currentCity');
        const dropsCity = hadCity && !receivedAnonData.currentCity;
        const dropsFlag = hadFlag && !receivedAnonData.sharedFields?.includes('currentCity');
        if (dropsCity || dropsFlag) {
          logConnectionError('anon_data_city_dropped', {
            chatRoomId,
            dropsCity,
            dropsFlag,
            incomingKeys: Object.keys(receivedAnonData).join(','),
            caller: (new Error().stack || '').split('\n').slice(2, 5).join(' | '),
          });
        }
      } catch (tripwireErr) {
        console.warn('[STORAGE] City tripwire check failed:', tripwireErr);
      }
    }

    const result = await setItem(dataKey, receivedAnonData);

    // Dual-write to Supabase
    upsertConnectionData(chatRoomId, { received_contact: receivedAnonData });

    return result;
  } catch (error) {
    console.error(`[STORAGE] Error saving receivedAnonData for chatRoomId: ${chatRoomId}`, error);
    return false;
  }
};

/**
 * Get received anonymous user data for a specific connection
 * @param {string} chatRoomId - Chat room ID for the connection
 * @returns {Promise<Object|null>} Anonymous user data or null if not found
 */
export const getReceivedAnonData = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] Missing chatRoomId for getting receivedAnonData');
      return null;
    }

    const dataKey = `${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`;
    return await getItem(dataKey);
  } catch (error) {
    console.error(`[STORAGE] Error getting receivedAnonData for chatRoomId: ${chatRoomId}`, error);
    return null;
  }
};

/**
 * Get manual contact data for a specific connection
 * @param {string} chatRoomId - Chat room ID for the manual contact (format: manual_[timestamp]_[nanoid])
 * @returns {Promise<Object|null>} Manual contact data or null if not found
 */
export const getManualContactData = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for getting manual contact data');
      return null;
    }

    const dataKey = `${MANUAL_CONTACT_DATA_PREFIX}${chatRoomId}`;
    return await getItem(dataKey);
  } catch (error) {
    console.error('[STORAGE] Error getting manual contact data:', error);
    return null;
  }
};

/**
 * Save manual contact data for a specific connection
 * @param {string} chatRoomId - Chat room ID for the manual contact (format: manual_[timestamp]_[nanoid])
 * @param {Object} manualContactData - Manual contact data to save
 * @returns {Promise<boolean>} Success status
 */
export const saveManualContactData = async (chatRoomId, manualContactData) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for saving manual contact data');
      return false;
    }

    if (!manualContactData) {
      console.error('[STORAGE] No manual contact data provided to save');
      return false;
    }

    // Validate name is present (required field)
    if (!manualContactData.name || manualContactData.name.trim() === '') {
      console.error('[STORAGE] Manual contact data must have a name');
      return false;
    }

    const dataKey = `${MANUAL_CONTACT_DATA_PREFIX}${chatRoomId}`;
    const dataToSave = {
      ...manualContactData,
      isManualContact: true, // Flag to identify manual contacts
    };
    const result = await setItem(dataKey, dataToSave);

    // Dual-write to Supabase
    upsertConnectionData(chatRoomId, { manual_contact: dataToSave, is_manual: true });

    return result;
  } catch (error) {
    console.error('[STORAGE] Error saving manual contact data:', error);
    return false;
  }
};

/**
 * Delete manual contact data for a specific connection
 * @param {string} chatRoomId - Chat room ID for the manual contact
 * @returns {Promise<boolean>} Success status
 */
export const deleteManualContactData = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for deleting manual contact data');
      return false;
    }

    const dataKey = `${MANUAL_CONTACT_DATA_PREFIX}${chatRoomId}`;
    await removeItem(dataKey);
    return true;
  } catch (error) {
    console.error('[STORAGE] Error deleting manual contact data:', error);
    return false;
  }
};

/**
 * Get all manual contact IDs by scanning localStorage
 * @returns {Promise<string[]>} Array of chatRoomIds for manual contacts
 */
export const getAllManualContactIds = async () => {
  try {
    const allKeys = Object.keys(localStorage);
    const manualContactKeys = allKeys.filter((key) => key.startsWith(MANUAL_CONTACT_DATA_PREFIX));

    // Extract chatRoomIds from the keys
    return manualContactKeys.map((key) => key.replace(MANUAL_CONTACT_DATA_PREFIX, ''));
  } catch (error) {
    console.error('[STORAGE] Error getting all manual contact IDs:', error);
    return [];
  }
};

/**
 * Clear connection-specific data for a chat room
 * @param {string} chatRoomId - Chat room ID to clear data for
 * @returns {Promise<boolean>} True if successful
 */
export const clearConnectionData = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chat room ID provided for clearing connection data');
      return false;
    }

    // Clear all connection-specific data
    const receivedProfileKey = `${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`;
    const metadataKey = `${CONNECTION_METADATA_PREFIX}${chatRoomId}`;
    const receivedAnonKey = `${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`;
    const anonDataKey = `${ANON_USER_DATA_PREFIX}${chatRoomId}`;
    const contactSharedKey = `contact_shared_${chatRoomId}`;
    const profileNotesKey = `${PROFILE_NOTES_PREFIX}${chatRoomId}`;
    const anonNotesKey = `${ANON_NOTES_PREFIX}${chatRoomId}`;
    const connectionGroupKey = `${CONNECTION_GROUP_PREFIX}${chatRoomId}`;
    const connectionTagsKey = `${CONNECTION_TAGS_PREFIX}${chatRoomId}`;
    const manualContactKey = `${MANUAL_CONTACT_DATA_PREFIX}${chatRoomId}`;

    await Promise.all([
      removeItem(receivedProfileKey),
      removeItem(metadataKey),
      removeItem(receivedAnonKey),
      removeItem(anonDataKey),
      removeItem(contactSharedKey),
      removeItem(profileNotesKey),
      removeItem(anonNotesKey),
      removeItem(connectionGroupKey),
      removeItem(connectionTagsKey),
      removeItem(manualContactKey),
    ]);

    return true;
  } catch (error) {
    console.error(`[STORAGE] Error clearing connection data for room ${chatRoomId}:`, error);
    return false;
  }
};

/**
 * Update profile data in localStorage
 * @param {Object} updates - Partial profile data to update
 * @returns {Promise<boolean>} Success status
 */
export const updateProfileData = async (updates) => {
  try {
    if (!updates) {
      console.error('[STORAGE] No updates provided for profile data');
      return false;
    }

    // Get current data and merge with updates
    const currentProfileData = (await getProfileData()) || {};
    const updatedData = { ...currentProfileData, ...updates };

    // Save the updated data
    return await saveProfileData(updatedData);
  } catch (error) {
    console.error('[STORAGE] Error updating profile data:', error);
    return false;
  }
};

/**
 * Clear anonymous data from localStorage
 * @param {string} [chatRoomId] - Optional chat room ID for specific connection
 * @returns {Promise<boolean>} Success status
 */
export const clearAnonData = async (chatRoomId) => {
  try {
    if (chatRoomId) {
      // Clear only chatRoomId-specific anonymous data
      const dataKey = `${ANON_USER_DATA_PREFIX}${chatRoomId}`;
      await removeItem(dataKey);
    } else {
      // Clear global anonymous data
      await removeItem(ANON_USER_KEY);
      // Also clear legacy keys for completeness
      await Promise.all([
        removeItem('anonUserName'),
        removeItem('anonUserPhone'),
        removeItem('anonUserEmail'),
        removeItem('anonUserInstagram'),
        removeItem('anonUserPhotoUrl'),
        removeItem('lastContactType'),
      ]);
    }

    return true;
  } catch (error) {
    console.error('[STORAGE] Error clearing anonymous data:', error);
    return false;
  }
};

/**
 * Get location permission status
 * @returns {Promise<boolean>} True if permission was granted
 */
export const getLocationPermission = async () => {
  try {
    const permission = await getItem(LOCATION_PERMISSION_KEY);
    return permission === 'granted';
  } catch (error) {
    console.error('Error getting location permission:', error);
    return false;
  }
};

/**
 * Save location permission status
 * @param {boolean} granted - Whether permission was granted
 * @returns {Promise<boolean>} Success status
 */
export const saveLocationPermission = async (granted) => {
  try {
    await setItem(LOCATION_PERMISSION_KEY, granted ? 'granted' : 'denied');
    return true;
  } catch (error) {
    console.error('Error saving location permission:', error);
    return false;
  }
};

/**
 * Get the current screen the user is viewing
 * @returns {Promise<string|null>} Current screen name or null if not set
 */
export const getCurrentScreen = async () => {
  try {
    return await getItem(CURRENT_SCREEN_KEY);
  } catch (error) {
    console.error('Error getting current screen:', error);
    return null;
  }
};

/**
 * Save the current screen the user is viewing
 * @param {string} screen - The screen name
 * @returns {Promise<boolean>} Success status
 */
export const setCurrentScreen = async (screen) => {
  try {
    return await setItem(CURRENT_SCREEN_KEY, screen);
  } catch (error) {
    console.error('Error saving current screen:', error);
    return false;
  }
};

/**
 * Get the selected contact type
 * @returns {Promise<string|null>} Selected contact type or null if not set
 */
export const getSelectedContactType = async () => {
  try {
    return await getItem(SELECTED_CONTACT_TYPE_KEY);
  } catch (error) {
    console.error('Error getting selected contact type:', error);
    return null;
  }
};

/**
 * Save the selected contact type
 * @param {string} contactType - The contact type
 * @returns {Promise<boolean>} Success status
 */
export const setSelectedContactType = async (contactType) => {
  try {
    return await setItem(SELECTED_CONTACT_TYPE_KEY, contactType);
  } catch (error) {
    console.error('Error saving selected contact type:', error);
    return false;
  }
};

/**
 * Get the contact message queue
 * @returns {Promise<Object>} The contact queue or empty object if not found
 */
export const getContactQueue = async () => {
  try {
    const queue = await getItem(MESSAGE_QUEUE_KEY);
    return queue || {};
  } catch (error) {
    console.error('Error getting contact queue:', error);
    return {};
  }
};

/**
 * Save the contact message queue
 * @param {Object} queue - The contact queue to save
 * @returns {Promise<boolean>} Success status
 */
export const saveContactQueue = async (queue) => {
  try {
    return await setItem(MESSAGE_QUEUE_KEY, queue);
  } catch (error) {
    console.error('Error saving contact queue:', error);
    return false;
  }
};

/**
 * Clear the contact message queue
 * @returns {Promise<boolean>} Success status
 */
export const clearContactQueue = async () => {
  try {
    await removeItem(MESSAGE_QUEUE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing contact queue:', error);
    return false;
  }
};

/**
 * Get the anonymous contact data
 * @returns {Promise<Object|null>} Contact data or null if not found
 */
export const getAnonContactData = async () => {
  try {
    return await getItem(ANON_CONTACT_DATA_KEY);
  } catch (error) {
    console.error('Error getting anon contact data:', error);
    return null;
  }
};

/**
 * Save the anonymous contact data
 * @param {Object} contactData - Contact data to save
 * @returns {Promise<boolean>} Success status
 */
export const saveAnonContactData = async (contactData) => {
  try {
    return await setItem(ANON_CONTACT_DATA_KEY, contactData);
  } catch (error) {
    console.error('Error saving anon contact data:', error);
    return false;
  }
};

/**
 * Get the app version from storage
 * @returns {Promise<string|null>} App version or null if not found
 */
export const getAppVersion = async () => {
  try {
    return await getItem(APP_VERSION_KEY);
  } catch (error) {
    console.error('Error getting app version:', error);
    return null;
  }
};

/**
 * Save the app version to storage
 * @param {string} version - App version to save
 * @returns {Promise<boolean>} Success status
 */
export const saveAppVersion = async (version) => {
  try {
    return await setItem(APP_VERSION_KEY, version);
  } catch (error) {
    console.error('Error saving app version:', error);
    return false;
  }
};

/**
 * Get connection group for a specific connection
 * @param {string} chatRoomId - The chatRoomId to get group for
 * @returns {Promise<string|null>} Connection group or null if not set
 */
export const getConnectionGroup = async (chatRoomId) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chatRoomId provided for getting connection group');
      return null;
    }
    const key = `${CONNECTION_GROUP_PREFIX}${chatRoomId}`;
    return await getItem(key);
  } catch (error) {
    console.error('[STORAGE] Error getting connection group:', error);
    return null;
  }
};

/**
 * Save connection group for a specific connection
 * @param {string} chatRoomId - The chatRoomId to save group for
 * @param {string} group - The group name ('Social' | 'Work' | 'Family' | 'Other')
 * @returns {Promise<boolean>} Success status
 */
export const saveConnectionGroup = async (chatRoomId, group) => {
  try {
    if (!chatRoomId) {
      console.error('[STORAGE] No chatRoomId provided for saving connection group');
      return false;
    }
    if (!group) {
      console.error('[STORAGE] No group provided for saving connection group');
      return false;
    }
    const key = `${CONNECTION_GROUP_PREFIX}${chatRoomId}`;
    return await setItem(key, group);
  } catch (error) {
    console.error('[STORAGE] Error saving connection group:', error);
    return false;
  }
};

/**
 * Get last selected group (global default for new connections)
 * @returns {Promise<string>} Last selected group or 'Social' as default
 */
export const getLastSelectedGroup = async () => {
  try {
    const group = await getItem(LAST_SELECTED_GROUP_KEY);
    return group || 'Social';
  } catch (error) {
    console.error('[STORAGE] Error getting last selected group:', error);
    return 'Social';
  }
};

/**
 * Save last selected group (global default for new connections)
 * @param {string} group - The group name to save as default
 * @returns {Promise<boolean>} Success status
 * @deprecated Use tags system instead
 */
export const saveLastSelectedGroup = async (group) => {
  try {
    if (!group) {
      console.error('[STORAGE] No group provided for saving last selected group');
      return false;
    }
    return await setItem(LAST_SELECTED_GROUP_KEY, group);
  } catch (error) {
    console.error('[STORAGE] Error saving last selected group:', error);
    return false;
  }
};

// ============================================
// SUPABASE PROFILE SYNC (for instant connection)
// ============================================

import { supabase, getAuthUserId, forgetHeldSession } from '../supabase';
import { getProfileUserId } from '../../utils/identityUtils';
import {
  isAnonymousAuthSession,
  setStoredTier,
  reconcileTierWithRow,
  TIER_SIMPLE,
  TIER_FULL,
} from '../../utils/userTier';
import { logDualWriteError, logConnectionError } from '../analytics/eventLogger';
import { SOURCE_BRANCH } from '../../utils/deployInfo';
import { seedMeeting } from '../meetings/meetingRows';
import { ALL_CONTACT_KEYS } from '../../utils/contactKeys';
import { isNewExperience, isLiveSharingReadOn } from '../../utils/newExperience';
import { applyContactSlotChange, clearedShareItemsFromPatch } from '../sharing/connectionSharing';
import { keepPhotoMeta } from '../photo/keepPhotoMeta';
import { isPersonColour } from '../people/personColour';
import { normalizePhone } from '../../utils/phoneNumber';
import { nanoid } from 'nanoid';
import { getPeerProfiles } from '../people/peerProfiles';
import { readProfileTier } from '../auth/profileTier';

// ============================================
// SUPABASE DUAL-WRITE HELPERS
// ============================================

/**
 * Assemble user_preferences object from localStorage keys
 * @returns {Object} User preferences for Supabase profiles.user_preferences
 */
const getUserPreferences = () => {
  return {
    connections_sort_mode: localStorage.getItem('connections_sort_mode') || 'all',
    profile_name_sharing: localStorage.getItem('profile_name_sharing') || 'full',
  };
};

/**
 * Upsert a row in connection_data table (fire-and-forget)
 * Skips if no profile_user_id (Anon user)
 * @param {string} chatRoomId - Connection identifier
 * @param {Object} fields - Columns to upsert (e.g., { notes: 'hello', tags: ['Social'] })
 * @param {string|null} profileUserIdOverride - If provided, write to this user's row instead of the current user's. Used for cross-user writes (e.g., sender updating peer's received_contact).
 */
export const upsertConnectionData = (chatRoomId, fields, profileUserIdOverride = null) => {
  const profileUserId = profileUserIdOverride || getProfileUserId();
  if (!profileUserId || !chatRoomId) return;

  // Include auth_user_id if an auth session exists (opportunistic, for Phase C RLS)
  const authToken = localStorage.getItem(
    Object.keys(localStorage).find((k) => k.startsWith('sb-') && k.endsWith('-auth-token')) || ''
  );
  let authUserId = null;
  if (authToken) {
    try {
      const parsed = JSON.parse(authToken);
      authUserId = parsed?.user?.id || null;
    } catch {
      // ignore parse errors
    }
  }

  supabase
    .from('connection_data')
    .upsert(
      {
        chat_room_id: chatRoomId,
        profile_user_id: profileUserId,
        ...(authUserId ? { auth_user_id: authUserId } : {}),
        ...fields,
        updated_at: new Date().toISOString(),
        source_branch: SOURCE_BRANCH,
      },
      { onConflict: 'chat_room_id,profile_user_id' }
    )
    .then(({ error }) => {
      if (error) {
        console.warn('[STORAGE] connection_data upsert failed:', error.message);
        logDualWriteError('connection_data', chatRoomId, error.message);
      }
    });
};

/**
 * Upsert a connection_views row and optionally seed a meetings row (fire-and-forget).
 * Called during exchange alongside confirmHostConnection/confirmGuestConnection.
 *
 * @param {string} chatRoomId - Connection identifier
 * @param {string} userId - The user's profile_user_id or anon_user_id
 * @param {Object} [options] - Optional fields
 * @param {string} [options.notes] - Notes about this connection
 * @param {string} [options.displayName] - Display name override
 * @param {Object} [options.metadata] - Connection metadata (for seeding a meeting)
 */
export const upsertConnectionView = async (chatRoomId, userId, options = {}) => {
  if (!chatRoomId || !userId) return;

  const authUserId = await getAuthUserId();

  const { data: cv, error: cvError } = await supabase
    .from('connection_views')
    .upsert(
      {
        chat_room_id: chatRoomId,
        user_id: userId,
        ...(authUserId ? { auth_user_id: authUserId } : {}),
        ...(options.notes !== undefined ? { notes: options.notes } : {}),
        ...(options.displayName !== undefined ? { display_name: options.displayName } : {}),
        source_branch: SOURCE_BRANCH,
      },
      { onConflict: 'chat_room_id,user_id' }
    )
    .select('id')
    .single();

  if (cvError) {
    console.warn('[STORAGE] connection_views upsert failed:', cvError.message);
    return;
  }

  // Seed this person's row for the meeting their record describes (keyed — a retried
  // exchange is a no-op). Fire-and-forget like the view upsert; the venue gate and the
  // key requirement live in meetingRows.
  const metadata = options.metadata;
  if (cv?.id && metadata?.meetingDate) {
    seedMeeting(cv.id, userId, metadata).catch((err) =>
      console.error('[STORAGE] meetings seed refused:', err.message)
    );
  }
};

/**
 * Read the owner's user-added contacts for a connection.
 *
 * DB-first: connection_views.user_contacts is the source of truth. These are
 * contacts the owner added on top of whatever the other person shared — always
 * the owner's own data, independent of the other person's account status.
 *
 * @param {string} chatRoomId - Connection identifier
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @returns {Promise<Object>} The user_contacts object (e.g. { phone, email }), or {} if none
 */
export const getUserContacts = async (chatRoomId, userId) => {
  if (!chatRoomId || !userId) return {};

  const { data, error } = await supabase
    .from('connection_views')
    .select('user_contacts')
    .eq('chat_room_id', chatRoomId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[STORAGE] getUserContacts failed:', error.message);
    return {};
  }

  return data?.user_contacts || {};
};

/**
 * Upsert the owner's user-added contacts (awaited, DB-first).
 *
 * Stores the full user_contacts object on the owner's connection_views row. Because
 * it's keyed by the owner's own user_id, it survives the other person converting
 * from Anon to Profile (the old anonData path lost these edits).
 *
 * @param {string} chatRoomId - Connection identifier
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @param {Object} contacts - The full user_contacts object to store
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const saveUserContacts = async (chatRoomId, userId, contacts) => {
  if (!chatRoomId || !userId) return false;

  const authUserId = await getAuthUserId();

  const { error } = await supabase.from('connection_views').upsert(
    {
      chat_room_id: chatRoomId,
      user_id: userId,
      ...(authUserId ? { auth_user_id: authUserId } : {}),
      user_contacts: contacts || {},
      source_branch: SOURCE_BRANCH,
    },
    { onConflict: 'chat_room_id,user_id' }
  );

  if (error) {
    console.warn('[STORAGE] saveUserContacts failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Read the owner's nickname for a connection.
 *
 * DB-first: connection_views.nickname is the source of truth. The nickname is the
 * owner's short "how I know them" descriptor — viewer-private, never shown to the
 * other person.
 *
 * @param {string} chatRoomId - Connection identifier
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @returns {Promise<string>} The nickname, or '' if none
 */
export const getNickname = async (chatRoomId, userId) => {
  if (!chatRoomId || !userId) return '';

  const { data, error } = await supabase
    .from('connection_views')
    .select('nickname')
    .eq('chat_room_id', chatRoomId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[STORAGE] getNickname failed:', error.message);
    return '';
  }

  return data?.nickname || '';
};

/**
 * Upsert the owner's nickname for a connection (awaited, DB-first).
 *
 * @param {string} chatRoomId - Connection identifier
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @param {string} nickname - The nickname to store ('' clears it)
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const saveNickname = async (chatRoomId, userId, nickname) => {
  if (!chatRoomId || !userId) return false;

  const authUserId = await getAuthUserId();

  const { error } = await supabase.from('connection_views').upsert(
    {
      chat_room_id: chatRoomId,
      user_id: userId,
      ...(authUserId ? { auth_user_id: authUserId } : {}),
      nickname: nickname || null,
      source_branch: SOURCE_BRANCH,
    },
    { onConflict: 'chat_room_id,user_id' }
  );

  if (error) {
    console.warn('[STORAGE] saveNickname failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Batch-read the owner's per-connection view fields in ONE query (People-list
 * paint): nicknames, and the contacts they typed for each person
 * (user_contacts — People's tile colour reads them, the same rule as Detail).
 *
 * @param {string[]} chatRoomIds - Connection identifiers
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @returns {Promise<{nicknames: Object, userContacts: Object}>} chat_room_id →
 *   nickname / user_contacts, non-empty only
 */
export const getViewFieldsForConnections = async (chatRoomIds, userId) => {
  const empty = { nicknames: {}, userContacts: {} };
  if (!chatRoomIds?.length || !userId) return empty;

  const { data, error } = await supabase
    .from('connection_views')
    .select('chat_room_id, nickname, user_contacts')
    .eq('user_id', userId)
    .in('chat_room_id', chatRoomIds);

  if (error) {
    console.warn('[STORAGE] getViewFieldsForConnections failed:', error.message);
    return empty;
  }

  const nicknames = {};
  const userContacts = {};
  for (const row of data || []) {
    if (row.nickname) nicknames[row.chat_room_id] = row.nickname;
    if (row.user_contacts && Object.keys(row.user_contacts).length > 0) {
      userContacts[row.chat_room_id] = row.user_contacts;
    }
  }
  return { nicknames, userContacts };
};

/**
 * Batch-read the owner's nicknames for a set of connections (the older lists).
 *
 * @param {string[]} chatRoomIds - Connection identifiers
 * @param {string} userId - Owner's profile_user_id or anon_user_id
 * @returns {Promise<Object>} Map of chat_room_id → nickname (non-empty only)
 */
export const getNicknamesForConnections = async (chatRoomIds, userId) => {
  if (!chatRoomIds?.length || !userId) return {};

  const { data, error } = await supabase
    .from('connection_views')
    .select('chat_room_id, nickname')
    .eq('user_id', userId)
    .in('chat_room_id', chatRoomIds)
    .not('nickname', 'is', null);

  if (error) {
    console.warn('[STORAGE] getNicknamesForConnections failed:', error.message);
    return {};
  }

  const map = {};
  for (const row of data || []) {
    if (row.nickname) map[row.chat_room_id] = row.nickname;
  }
  return map;
};

/**
 * Upload a profile photo to Supabase Storage — always a NEW file in the
 * person's folder (`<id>/<new id>.jpg`). Photos are add-only (open-access
 * lockdown, Decision 2): nobody, the owner included, can replace an existing
 * file; which photo shows is the profile's photo_url, set by its owner.
 * @param {string} profileUserId - The profile user's ID (the folder; a
 *   `temp_…` id before sign-up)
 * @param {Blob} imageBlob - The image blob to upload
 * @returns {Promise<string|null>} Public URL of uploaded photo, or null on failure
 */
export const uploadProfilePhoto = async (profileUserId, imageBlob) => {
  try {
    if (!profileUserId || !imageBlob) {
      console.error('[STORAGE] Missing profileUserId or imageBlob for photo upload');
      return null;
    }

    const filename = `${profileUserId}/${nanoid(12)}.jpg`;

    // A new file every time — the storage rules refuse any overwrite
    const { error } = await supabase.storage.from('profile-photos').upload(filename, imageBlob, {
      contentType: 'image/jpeg',
    });

    if (error) {
      console.error('[STORAGE] Photo upload failed:', error);
      return null;
    }

    // Get public URL with cache-busting parameter
    // Chrome aggressively caches images at the same URL, so we add a timestamp
    // to force re-fetch when the image is updated
    const {
      data: { publicUrl },
    } = supabase.storage.from('profile-photos').getPublicUrl(filename);

    const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
    console.log('[STORAGE] Photo uploaded to:', cacheBustedUrl);
    return cacheBustedUrl;
  } catch (error) {
    console.error('[STORAGE] Error uploading profile photo:', error);
    return null;
  }
};

/**
 * Convert a base64 data URL to a Blob and upload to Supabase Storage
 * Used for migrating existing base64 photos to Storage URLs
 * @param {string} profileUserId - The profile user's ID
 * @param {string} base64DataUrl - The base64 data URL (e.g., "data:image/jpeg;base64,...")
 * @returns {Promise<string|null>} Public URL of uploaded photo, or null on failure
 */
export const migrateBase64Photo = async (profileUserId, base64DataUrl) => {
  try {
    if (!base64DataUrl?.startsWith('data:image')) {
      return null;
    }

    // Convert data URL to blob
    const response = await fetch(base64DataUrl);
    const blob = await response.blob();

    // Upload and get URL
    return await uploadProfilePhoto(profileUserId, blob);
  } catch (error) {
    console.error('[STORAGE] Error migrating base64 photo:', error);
    return null;
  }
};

/**
 * Sync profile data to Supabase profiles table
 * Called when profile is saved or updated
 * @param {Object} profileData - The profile data to sync
 * @returns {Promise<boolean>} Success status
 */
/**
 * Sync platform_links from profileData. Delete-and-reinsert pattern:
 * removes all rows for this user, then inserts one row per non-empty contact field.
 * Secondary variants (phone2, email2) use the base type with sort_order=1.
 */
const syncPlatformLinks = async (profileUserId, profileData) => {
  try {
    const rows = [];
    for (const key of ALL_CONTACT_KEYS) {
      const value = profileData[key];
      if (!value || (typeof value === 'string' && value.trim() === '')) continue;

      const isSecondary = key.endsWith('2');
      const type = isSecondary ? key.slice(0, -1) : key;
      rows.push({
        profile_user_id: profileUserId,
        type,
        value: typeof value === 'string' ? value.trim() : value,
        sort_order: isSecondary ? 1 : 0,
        is_primary: false,
        source_branch: SOURCE_BRANCH,
      });
    }

    await supabase.from('platform_links').delete().eq('profile_user_id', profileUserId);

    if (rows.length > 0) {
      const { error } = await supabase.from('platform_links').insert(rows);
      if (error) {
        console.error('[STORAGE] platform_links sync failed:', error);
        logDualWriteError('platform_links', profileUserId, error.message);
      }
    }
  } catch (error) {
    console.error('[STORAGE] Error syncing platform_links:', error);
    logDualWriteError('platform_links', null, error.message);
  }
};

export const syncProfileToSupabase = async (
  profileData,
  { ownPhotoMeta = false, ownColour = false } = {}
) => {
  try {
    // Use canonical profileUserId from standalone key if not in profile object
    const profileUserId = profileData?.profileUserId || localStorage.getItem('profile_user_id');

    console.log('[STORAGE] syncProfileToSupabase called with:', {
      profileUserId: profileUserId || 'none',
      name: profileData?.name || 'none',
      hasPhoto: !!profileData?.photoUrl,
      photoIsBase64: profileData?.photoUrl?.startsWith('data:image') || false,
    });

    if (!profileUserId) {
      console.error('[STORAGE] Missing profileUserId for Supabase sync');
      return false;
    }

    // Guard: Skip upsert if name is missing — avoids NOT NULL constraint violation
    // syncProfileToSupabase() can fire before user has entered their name
    if (!profileData?.name) {
      console.warn('[STORAGE] Skipping profile sync — name is missing');
      return false;
    }

    // Non-destructive merge: fetch the saved blob and merge the incoming object over it.
    //   key absent / undefined / null → keep the saved value (omission can't delete)
    //   explicit '' → the user cleared the field; honor it
    // Callers may sync partial or stale objects without wiping fields they don't know about.
    const { data: existingRow } = await supabase
      .from('profiles')
      .select('profile_data')
      .eq('profile_user_id', profileUserId)
      .maybeSingle();

    const merged = { ...(existingRow?.profile_data || {}) };
    for (const [key, value] of Object.entries(profileData)) {
      if (value !== undefined && value !== null) merged[key] = value;
    }
    merged.profileUserId = profileUserId;
    // A stale copy's photoMeta never overwrites the stored one — only the
    // capture path (saveOwnPhotoMeta) writes it outright (keepPhotoMeta).
    if (!ownPhotoMeta) {
      const photoMeta = keepPhotoMeta(
        profileData.photoMeta,
        existingRow?.profile_data?.photoMeta,
        merged.photoUrl
      );
      if (photoMeta) merged.photoMeta = photoMeta;
    }
    // Same for the chosen colour — only saveOwnColour writes it outright.
    const storedColour = existingRow?.profile_data?.colour;
    if (!ownColour && storedColour) merged.colour = storedColour;

    // Validate: if phone is already taken by a different profile, block the save.
    // Phone uniqueness is enforced by DB constraint (profiles_phone_unique),
    // but we check explicitly to return a clear error to the caller.
    // Anonymous-session exception (device pass 2, 2026-08-08: an SP mint died
    // on a number an old Full profile owned): an SP's phone is SHARED CONTACT
    // INFO, not an identity claim — login is the Full line, and the phone
    // COLUMN is the OTP-login index. On a collision from an anonymous session
    // the column stays with its verified owner; the blob keeps the SP's
    // number for cards and recovery, and the claim resolves at OTP time
    // (compile-by-phone-evidence).
    let contestedPhone = false;
    if (merged.phone) {
      const normalizedLookup = normalizePhone(merged.phone);
      if (normalizedLookup) {
        // Yes/no only (open-access lockdown): whose number it is never
        // comes back — another person's profile row isn't readable.
        const { data: takenByOther } = await supabase.rpc('phone_taken_by_other', {
          p_phone: normalizedLookup,
        });

        if (takenByOther === true) {
          if (isAnonymousAuthSession() === true) {
            console.warn(
              '[STORAGE] Phone owned by another profile — anonymous sync keeps it out of the unique column'
            );
            contestedPhone = true;
          } else {
            console.warn('[STORAGE] Phone taken by another profile');
            return { error: 'phone_taken' };
          }
        }
      }
    }

    // Check if photo needs migration (base64 → Storage URL)
    let photoUrl = merged.photoUrl;
    if (photoUrl?.startsWith('data:image')) {
      console.log('[STORAGE] Migrating base64 photo to Supabase Storage...');
      const storageUrl = await migrateBase64Photo(profileUserId, photoUrl);
      if (storageUrl) {
        photoUrl = storageUrl;
        merged.photoUrl = storageUrl;
        // Update localStorage with the new URL
        const currentProfile = await getProfileData();
        if (currentProfile) {
          await saveProfileData({ ...currentProfile, photoUrl: storageUrl });
        }
      }
    }

    // Normalize phone to E.164 for the phone column (OTP lookup requires consistent format)
    // PhoneInputField outputs E.164, but legacy/demo data may have formatted numbers

    // Upsert to Supabase profiles table (includes full profile_data + user_preferences)
    console.log('[STORAGE] Upserting to Supabase profiles table...');
    // Include auth_user_id if an auth session exists (opportunistic, for Phase C RLS)
    let authUserIdForProfile = null;
    const authTokenKey = Object.keys(localStorage).find(
      (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
    );
    if (authTokenKey) {
      try {
        const parsed = JSON.parse(localStorage.getItem(authTokenKey));
        authUserIdForProfile = parsed?.user?.id || null;
      } catch {
        // ignore
      }
    }

    const normalizedPhone = normalizePhone(merged.phone);

    // Columns derive from the merged blob under the same contract:
    //   undefined → omit the column entirely (the DB keeps its value — this also
    //   protects legacy rows whose column holds a value the blob never carried)
    //   '' → null (an honored clear); anything else → the value
    const column = (v) => (v === '' ? null : v);
    const upsertData = {
      profile_user_id: profileUserId,
      name: merged.name,
      // Contested phone (anonymous session): column omitted — the verified
      // owner keeps the login index; the blob below still carries the number
      phone: merged.phone === undefined || contestedPhone ? undefined : normalizedPhone,
      email: column(merged.email),
      instagram: column(merged.instagram),
      photo_url: column(photoUrl),
      profile_data: merged,
      user_preferences: getUserPreferences(),
      first_name: merged.name ? merged.name.split(' ')[0] : null,
      about: column(merged.whatIDo),
      title: column(merged.role),
      org: column(merged.organization),
      hometown: column(merged.currentCity),
      // account_status is NOT sent (f/tier-integrity): the server owns the
      // tier — mint writes 'anon', conversion writes 'full' (migration 038's
      // trigger ignores any client value).
      ...(authUserIdForProfile ? { auth_user_id: authUserIdForProfile } : {}),
      // converted_from_anon_id is NOT sent (f/identity-ownership): the server
      // stamps it at the Simple mint from the guest ids this session owns
      // (rekey_simple_profile v2, migration 037) and keeps any client value
      // out (036). The blob's convertedFromAnonId stays local-only.
      updated_at: new Date().toISOString(),
      source_branch: SOURCE_BRANCH,
    };
    Object.keys(upsertData).forEach((key) => {
      if (upsertData[key] === undefined) delete upsertData[key];
    });
    console.log('[STORAGE] Upsert data:', { ...upsertData, profile_data: '[object]' });

    const { error } = await supabase.from('profiles').upsert(upsertData, {
      onConflict: 'profile_user_id',
    });

    if (error) {
      console.error('[STORAGE] Supabase profile sync failed:', error);
      return false;
    }

    console.log('[STORAGE] Profile synced to Supabase successfully:', profileUserId);

    // Fire-and-forget: sync contact fields to platform_links (Phase 1b expand).
    // Pass the MERGED blob — platform_links is rebuilt from what it's given
    // (delete-then-insert), so a partial object would wipe links by omission.
    syncPlatformLinks(profileUserId, merged);

    return true;
  } catch (error) {
    console.error('[STORAGE] Error syncing profile to Supabase:', error);
    return false;
  }
};

/**
 * Fetch profile data from Supabase profiles table
 * Used to verify localStorage and database are in sync before showing QR
 * @param {string} profileUserId - The profile user ID to fetch
 * @returns {Promise<Object|null>} Profile data from database or null if not found
 */
export const getProfileFromDatabase = async (profileUserId) => {
  try {
    if (!profileUserId) {
      console.log('[STORAGE] getProfileFromDatabase: No profileUserId provided');
      return null;
    }

    console.log('[STORAGE] Fetching profile from database:', profileUserId);
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'profile_user_id, name, phone, email, instagram, photo_url, profile_data, user_preferences, updated_at'
      )
      .eq('profile_user_id', profileUserId)
      .maybeSingle();

    if (error) {
      console.error('[STORAGE] Error fetching profile from database:', error);
      return null;
    }

    if (!data) {
      console.log('[STORAGE] Profile not found in database:', profileUserId);
      return null;
    }

    console.log('[STORAGE] Profile fetched from database:', {
      profileUserId: data.profile_user_id,
      name: data.name,
      hasPhotoUrl: !!data.photo_url,
    });

    return data;
  } catch (error) {
    console.error('[STORAGE] Error in getProfileFromDatabase:', error);
    return null;
  }
};

/**
 * Check if a single profile has been deleted, through the checked peer read.
 * Used by ConnectionStateContext for per-connection detection on direct navigation.
 * Distinguishes "gone" from "unknown / query error": peerProfileDeleted is set
 * only on a confirmed gone, and cleared only on a confirmed exists.
 * @param {string} profileUserId - The profile user ID to check
 * @returns {Promise<true|false|null>} true = deleted, false = exists, null = query error (skip)
 */
export const checkDeletedProfile = async (profileUserId) => {
  if (!profileUserId) return null;
  // Checked peer read (open-access lockdown): no row = not one of my peers =
  // unknown, never "deleted" — the latch only fires on profileExists false.
  const { rows, error } = await getPeerProfiles([profileUserId]);
  if (error) return null;
  const row = rows.get(profileUserId);
  return row ? !row.profileExists : null;
};

/**
 * Self-rooms (f/simple-merge, migration 039): rooms where this account sits on
 * both sides because one of its own Simple profiles merged into it — the
 * person had met themselves. Hidden from People (decision 5; messages kept):
 * this latches `selfRoom` on each such room's record (one-way, like
 * peerProfileDeleted) and returns their ids for the caller to drop.
 * Only rooms whose cached peer now reads 'merged' are candidates (one small
 * read), and only those are confirmed against the room itself — so a peer who
 * merely met the merged profile never loses the room (theirs now shows the
 * account).
 * @param {Array} connections - People rows ({ chatRoomId, anonData })
 * @param {string} myProfileUserId
 * @returns {Promise<string[]>} chatRoomIds now latched as self-rooms
 */
export const latchSelfRooms = async (connections, myProfileUserId) => {
  // Only rooms already holding a peer id are candidates. The retired (merged)
  // profile's row is no longer readable directly (open-access lockdown), so
  // the room itself decides: me on both sides is a self-room, whoever the
  // cached peer was. Same answer the merged pre-filter gave — it only narrowed
  // which rooms were asked about.
  const candidates = connections.filter(
    (c) => c.anonData?.senderProfileUserId && !c.anonData?.selfRoom
  );
  if (!myProfileUserId || candidates.length === 0) return [];

  const { data: rooms, error: roomError } = await supabase
    .from('connections')
    .select('chat_room_id')
    .in(
      'chat_room_id',
      candidates.map((c) => c.chatRoomId)
    )
    .eq('host_user_id', myProfileUserId)
    .eq('guest_user_id', myProfileUserId);
  if (roomError || !rooms?.length) return [];

  const selfIds = new Set(rooms.map((r) => r.chat_room_id));
  for (const c of candidates) {
    if (selfIds.has(c.chatRoomId))
      saveReceivedAnonData(c.chatRoomId, { ...c.anonData, selfRoom: true });
  }
  return [...selfIds];
};

/**
 * The deleted mark follows the server, both ways, from ONE checked peer read:
 * `deleted` = unmarked rooms whose peer is confirmed gone (set the mark);
 * `revived` = marked rooms whose peer is confirmed to exist (clear it — the
 * lockdown's closed profiles read once made the previous app mark every peer
 * deleted; dev 2026-10-07: 17 records). An id the server doesn't know as my
 * peer (no row) is left alone either way; a failed read changes nothing.
 * Query-only: the caller applies the changes (applyDeletedMarkChange).
 * @param {Array} connections - People rows ({ chatRoomId, anonData })
 * @returns {Promise<{deleted: string[], revived: string[]}>} chatRoomIds
 */
export const reconcileDeletedMarks = async (connections) => {
  const none = { deleted: [], revived: [] };
  const withPeer = connections.filter((c) => c.anonData?.senderProfileUserId);
  if (withPeer.length === 0) return none;
  const { rows, error } = await getPeerProfiles(
    withPeer.map((c) => c.anonData.senderProfileUserId)
  );
  if (error) return none; // no false positives — the next load retries
  const exists = (c) => rows.get(c.anonData.senderProfileUserId)?.profileExists;
  return {
    deleted: withPeer
      .filter((c) => !c.anonData.peerProfileDeleted && exists(c) === false)
      .map((c) => c.chatRoomId),
    revived: withPeer
      .filter((c) => c.anonData.peerProfileDeleted && exists(c) === true)
      .map((c) => c.chatRoomId),
  };
};

/**
 * The peer record after a reconcileDeletedMarks answer, or null when this room
 * is unchanged. Persists the change; the caller puts it into its state.
 * @param {{chatRoomId: string, anonData: Object}} conn
 * @param {{deleted: string[], revived: string[]}} changes
 * @returns {Object|null} the new anonData, or null
 */
export const applyDeletedMarkChange = (conn, { deleted, revived }) => {
  let anonData = null;
  if (deleted.includes(conn.chatRoomId)) anonData = { ...conn.anonData, peerProfileDeleted: true };
  else if (revived.includes(conn.chatRoomId)) anonData = withoutDeletedMark(conn.anonData);
  if (anonData) saveReceivedAnonData(conn.chatRoomId, anonData);
  return anonData;
};

/** A peer record without the deleted mark (the mark is the only change). */
export const withoutDeletedMark = (anonData) => {
  const rest = { ...(anonData || {}) };
  delete rest.peerProfileDeleted;
  return rest;
};

/**
 * Batch-check which connections the PEER has left (per-side delete, CD #15).
 * Query-only sibling of getDeletedProfileIds: returns chatRoomIds of connections
 * whose OTHER side set its *_left_at on connections (so the readers stopped
 * delivering their live data to me). The caller latches peerLeft on those — my
 * copy then freezes and becomes fully editable, exactly like a deleted-account
 * peer (see isPeerGone).
 *
 * My OWN side being set never lands here: deleting a connection removes my local
 * record, so it isn't in the list passed in. The peer's side is the one that
 * isn't mine; when neither id is mine (can't tell), "either side left" is the
 * fallback — my own side would already have dropped the record.
 * @param {Array} connections - People rows ({ chatRoomId, anonData })
 * @param {string} myProfileUserId
 * @returns {Promise<string[]>} chatRoomIds to latch peerLeft
 */
export const getLeftConnectionIds = async (connections, myProfileUserId) => {
  const toCheck = connections.filter(
    (c) => c.chatRoomId && !c.anonData?.peerLeft && !c.anonData?.peerProfileDeleted
  );
  if (!myProfileUserId || toCheck.length === 0) return [];

  const roomIds = [...new Set(toCheck.map((c) => c.chatRoomId))];
  const { data, error } = await supabase
    .from('connections')
    .select('chat_room_id, host_user_id, guest_user_id, host_left_at, guest_left_at')
    .in('chat_room_id', roomIds);
  if (error) return []; // Skip — no false positives. Next load retries.

  // The query was keyed on chat_room_id IN roomIds (⊆ toCheck's rooms), so the
  // matched set IS the answer — no need to re-walk toCheck.
  const peerGone = new Set(
    (data || [])
      .filter((r) => {
        if (r.host_user_id === myProfileUserId) return r.guest_left_at != null;
        if (r.guest_user_id === myProfileUserId) return r.host_left_at != null;
        return r.host_left_at != null || r.guest_left_at != null;
      })
      .map((r) => r.chat_room_id)
  );
  return [...peerGone];
};

/**
 * Ensure profile is synced to Supabase (migration for existing users)
 * Call on app load for Profile users
 * @returns {Promise<boolean>} Success status (false if no profile or sync failed)
 */
export const ensureProfileSynced = async () => {
  try {
    console.log('[STORAGE] ensureProfileSynced called');

    // Use canonical identity from localStorage (not profileData object which may be stale)
    const canonicalProfileUserId = getProfileUserId();
    const localProfile = await getProfileData();

    console.log('[STORAGE] Local profile data:', {
      exists: !!localProfile,
      canonicalProfileUserId: canonicalProfileUserId || 'none',
      objectProfileUserId: localProfile?.profileUserId || 'none',
      name: localProfile?.name || 'none',
    });

    if (!canonicalProfileUserId) {
      // No profile identity, nothing to sync
      console.log('[STORAGE] No profile_user_id found in localStorage, skipping sync');
      return false;
    }

    if (!localProfile || !localProfile.name) {
      // Profile incomplete (e.g., PhotoSelectionOverlay created ID before user clicked Done)
      console.log('[STORAGE] Profile incomplete (no name), skipping sync');
      return false;
    }

    // Check for pending initial sync (interrupted Anon→Profile conversion)
    if (localStorage.getItem('pending_initial_sync')) {
      console.log('[STORAGE] Pending initial sync detected, running full sync...');
      await syncAllConnectionsToSupabase(canonicalProfileUserId);
    }

    // Check if already in Supabase
    console.log('[STORAGE] Checking Supabase for profile:', canonicalProfileUserId);
    // Tier + owner through profile_tier_check (open-access lockdown: a row
    // another session owns can't be read directly — and that is exactly the
    // case the checks below exist for). Photo/profile_data only when mine.
    // A not-yet-synced profile is expected — data null.
    const tier = await readProfileTier(canonicalProfileUserId);
    let error = tier.error;
    let data = tier.row;
    if (data?.isMine) {
      const own = await supabase
        .from('profiles')
        .select('photo_url, profile_data')
        .eq('profile_user_id', canonicalProfileUserId)
        .maybeSingle();
      error = own.error;
      data = { ...data, ...(own.data || {}) };
    }

    console.log('[STORAGE] Supabase query result:', {
      hasData: !!data,
      error: error?.message || 'none',
      errorCode: error?.code || 'none',
    });

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = "no rows returned" (expected if not synced yet)
      console.error('[STORAGE] Error checking Supabase profile:', error);
      return false;
    }

    // Tier reconcile — the database is the record, the phone's copy follows
    // it (auth.md § Account Tiers). A takeover (converted on another device)
    // belongs to useConversionCheck: skip the mismatch → OTP redirect below,
    // which would otherwise race it and kick the newly installed app.
    if (data) {
      const tierAction = reconcileTierWithRow(data, getAuthUserId());
      if (tierAction === 'takeover') {
        console.log('[STORAGE] Converted on another device — takeover owns this boot');
        return false;
      }
      if (tierAction === 'promote' || tierAction === 'demote') {
        console.log('[STORAGE] Tier copy corrected from the database:', tierAction);
        setStoredTier(tierAction === 'promote' ? TIER_FULL : TIER_SIMPLE);
        window.location.reload();
        return false; // Won't reach due to reload
      }
    }

    // UUID mismatch detection: if the DB has an auth_user_id that doesn't match
    // the current session's auth UUID, the session expired and a new one was created.
    // Force OTP re-verification so the RPC can relink all tables.
    // Skip if flag already set — DemoGate will show OTP screen on next render.
    if (data?.auth_user_id && !sessionStorage.getItem('pending_otp_required')) {
      const currentAuthId = getAuthUserId();
      if (currentAuthId && data.auth_user_id !== currentAuthId) {
        console.warn(
          '[STORAGE] UUID mismatch detected — DB:',
          data.auth_user_id,
          'Session:',
          currentAuthId
        );
        sessionStorage.setItem('pending_otp_required', 'true');
        window.location.reload();
        return false; // Won't reach due to reload
      }
    }

    if (!data) {
      // Not in Supabase yet, sync now
      console.log('[STORAGE] Profile NOT in Supabase, migrating now:', canonicalProfileUserId);
      const result = await syncProfileToSupabase(localProfile);
      console.log('[STORAGE] Migration result:', result);
      return result;
    }

    // Already synced — check if photo or profile_data needs migration (only
    // a row this session owns can be written)
    const needsSync =
      data.isMine &&
      ((localProfile?.photoUrl?.startsWith('data:image') &&
        !data.photo_url?.includes('.supabase.co')) ||
        !data.profile_data); // Backfill profile_data if missing (existing profiles pre-dual-write)

    if (needsSync) {
      console.log('[STORAGE] Profile exists but needs sync (photo or profile_data missing)');
      return await syncProfileToSupabase(localProfile);
    }

    console.log('[STORAGE] Profile already synced:', canonicalProfileUserId);
    return true;
  } catch (error) {
    console.error('[STORAGE] Error ensuring profile sync:', error);
    return false;
  }
};

// ============================================
// LAST READ TIMESTAMP (for unread indicators)
// ============================================

/**
 * Get the last read timestamp for a chat room
 * Used to determine if there are unread messages
 * @param {string} chatRoomId - The chatRoomId to get last read timestamp for
 * @returns {string|null} ISO timestamp string or null if never read
 */
export const getLastReadTimestamp = (chatRoomId) => {
  if (!chatRoomId) return null;
  return localStorage.getItem(`${LAST_READ_PREFIX}${chatRoomId}`);
};

/**
 * Set the last read timestamp for a chat room to now
 * Called when user enters a chat to mark all messages as read
 * @param {string} chatRoomId - The chatRoomId to mark as read
 */
export const setLastReadTimestamp = (chatRoomId) => {
  if (!chatRoomId) return;
  localStorage.setItem(`${LAST_READ_PREFIX}${chatRoomId}`, new Date().toISOString());
};

/**
 * Clear all user data from storage
 * This is a comprehensive function that combines all individual clear functions
 * for easier data reset or logout functionality.
 *
 * @returns {Promise<boolean>} Success status
 */
export const clearAllData = async () => {
  try {
    // Clear all active Supabase channel subscriptions first
    // This prevents orphaned connections that could receive stale messages
    const { clearAllChannels } = await import('../chat/chatService');
    await clearAllChannels();

    // Clear Supabase auth session — dispatch but DON'T await. supabase.auth.signOut() makes a
    // network call that can hang on Android, and awaiting it blocked the localStorage clear
    // below (the real goal). Fire-and-forget; the local clear is what matters.
    try {
      const { signOut } = await import('../auth/otpAuth');
      signOut().catch(() => {});
    } catch {
      // otpAuth may not be loaded yet, that's fine
    }

    forgetHeldSession();
    localStorage.clear();
    sessionStorage.clear();

    return true;
  } catch (error) {
    console.error('Error clearing all data:', error);
    return false;
  }
};

// ============================================
// CONNECTION-LEVEL STORAGE FUNCTIONS (with dual-write)
// These replace direct localStorage calls in context/overlay code
// ============================================

/**
 * Save contact sharing status for a connection
 * Replaces direct localStorage.setItem in ConnectionStateContext
 * @param {string} chatRoomId - Connection identifier
 * @param {boolean} shared - Whether contact was shared
 */
export const saveContactSharingStatus = async (chatRoomId, shared) => {
  if (!chatRoomId) return;
  if (shared) {
    localStorage.setItem(`contact_shared_${chatRoomId}`, 'true');
    localStorage.setItem('any_contact_shared', 'true');
  }
  // Dual-write to Supabase
  upsertConnectionData(chatRoomId, { contact_shared: !!shared });
};

/**
 * Save contact saved-to-phone status for a connection
 * Replaces direct localStorage.setItem in ConnectionStateContext
 * @param {string} chatRoomId - Connection identifier
 * @param {boolean} saved - Whether contact was saved to phone
 */
export const saveContactSavedStatus = async (chatRoomId, saved) => {
  if (!chatRoomId) return;
  if (saved) {
    localStorage.setItem(`contact_added_${chatRoomId}`, 'true');
  }
  // Dual-write to Supabase
  upsertConnectionData(chatRoomId, { contact_added: !!saved });
};

/**
 * Save sharing config for a connection (batches 3 writes into 1)
 * Replaces 3 separate localStorage.setItem calls in SharingSelectionOverlay
 * @param {string} chatRoomId - Connection identifier
 * @param {Object} config
 * @param {string[]} config.sentSharedFields - Array of shared field keys
 * @param {string} config.nameSharing - 'first' or 'full'
 * @param {boolean} config.contactCardSent - Whether card was sent
 */
export const saveSharingConfig = async (chatRoomId, config) => {
  if (!chatRoomId) return;
  const { sentSharedFields, nameSharing, contactCardSent } = config;

  // localStorage writes
  if (sentSharedFields) {
    localStorage.setItem(`sent_shared_fields_${chatRoomId}`, JSON.stringify(sentSharedFields));
  }
  if (nameSharing) {
    localStorage.setItem(`name_sharing_${chatRoomId}`, nameSharing);
  }
  if (contactCardSent !== undefined) {
    localStorage.setItem(`contact_card_sent_${chatRoomId}`, String(contactCardSent));
  }

  // Supabase upsert — only include fields that were explicitly passed.
  // Passing all 3 every time overwrites: the contactCardSent-only call
  // would null out sent_shared_fields and name_sharing.
  const upsertData = {};
  if (sentSharedFields !== undefined) upsertData.sent_shared_fields = sentSharedFields;
  if (nameSharing !== undefined) upsertData.name_sharing = nameSharing;
  if (contactCardSent !== undefined) upsertData.contact_card_sent = !!contactCardSent;
  if (Object.keys(upsertData).length > 0) {
    upsertConnectionData(chatRoomId, upsertData);
  }
};

/**
 * The per-connection sent-share ledger, read beside its writer (above) —
 * components must not parse the localStorage key themselves.
 * @param {string} chatRoomId
 * @returns {string[]} shared field keys, [] when unset/unreadable
 */
export const getSentSharedFields = (chatRoomId) => {
  if (!chatRoomId) return [];
  try {
    return JSON.parse(localStorage.getItem(`sent_shared_fields_${chatRoomId}`) || '[]');
  } catch {
    return [];
  }
};

/**
 * Whether this connection gets the full name or the first name only — the
 * per-connection setting, else the last global choice, else full.
 * @param {string} chatRoomId
 * @returns {boolean} true = full name
 */
export const getNameSharingFull = (chatRoomId) => {
  const ns =
    (chatRoomId && localStorage.getItem(`name_sharing_${chatRoomId}`)) ||
    localStorage.getItem('profile_name_sharing');
  return ns ? ns === 'full' : true;
};

// ============================================
// FULL SYNC (post Anon→Profile conversion)
// ============================================

/**
 * Sync all connection data from localStorage to Supabase
 * Called after Anon→Profile conversion to capture pre-conversion data
 * @param {string} profileUserId - The newly created profile user ID
 * @param {{ includeProfile?: boolean }} [options] - false when the caller just
 *   synced the profile row itself (the Simple mint)
 */
export const syncAllConnectionsToSupabase = async (
  profileUserId,
  { includeProfile = true } = {}
) => {
  if (!profileUserId) return;

  try {
    console.log('[STORAGE] syncAllConnectionsToSupabase starting for:', profileUserId);
    const allKeys = Object.keys(localStorage);

    // Find all connection chatRoomIds (from metadata keys + manual contact keys)
    const metadataIds = allKeys
      .filter((k) => k.startsWith(CONNECTION_METADATA_PREFIX))
      .map((k) => k.replace(CONNECTION_METADATA_PREFIX, ''));
    const manualIds = allKeys
      .filter((k) => k.startsWith(MANUAL_CONTACT_DATA_PREFIX))
      .map((k) => k.replace(MANUAL_CONTACT_DATA_PREFIX, ''));
    const chatRoomIds = [...new Set([...metadataIds, ...manualIds])];

    console.log('[STORAGE] Found', chatRoomIds.length, 'connections to sync');

    // Owner stamp: RLS inserts need auth_user_id = auth.uid() — without it
    // every row was refused and this sync failed silently (found f/simple-merge)
    const authUserId = await getAuthUserId();

    // Build upsert rows
    const rows = [];
    for (const chatRoomId of chatRoomIds) {
      const row = {
        chat_room_id: chatRoomId,
        profile_user_id: profileUserId,
        ...(authUserId ? { auth_user_id: authUserId } : {}),
        updated_at: new Date().toISOString(),
        source_branch: SOURCE_BRANCH,
      };

      // Read each localStorage key and map to column
      const metadata = await getItem(`${CONNECTION_METADATA_PREFIX}${chatRoomId}`);
      if (metadata) row.metadata = metadata;

      const receivedContact =
        (await getItem(`${RECEIVED_ANON_DATA_PREFIX}${chatRoomId}`)) ||
        (await getItem(`${RECEIVED_PROFILE_DATA_PREFIX}${chatRoomId}`));
      if (receivedContact) row.received_contact = receivedContact;

      const manualContact = await getItem(`${MANUAL_CONTACT_DATA_PREFIX}${chatRoomId}`);
      if (manualContact) {
        row.manual_contact = manualContact;
        row.is_manual = true;
      }

      const notes = await getItem(`${PROFILE_NOTES_PREFIX}${chatRoomId}`);
      if (notes) row.notes = notes;

      const sentSharedFields = await getItem(`sent_shared_fields_${chatRoomId}`);
      if (sentSharedFields) row.sent_shared_fields = sentSharedFields;

      const nameSharing = localStorage.getItem(`name_sharing_${chatRoomId}`);
      if (nameSharing) row.name_sharing = nameSharing;

      const contactShared = localStorage.getItem(`contact_shared_${chatRoomId}`);
      if (contactShared === 'true') row.contact_shared = true;

      const contactAdded = localStorage.getItem(`contact_added_${chatRoomId}`);
      if (contactAdded === 'true') row.contact_added = true;

      const contactCardSent = localStorage.getItem(`contact_card_sent_${chatRoomId}`);
      if (contactCardSent === 'true') row.contact_card_sent = true;

      rows.push(row);
    }

    if (rows.length > 0) {
      const { error } = await supabase
        .from('connection_data')
        .upsert(rows, { onConflict: 'chat_room_id,profile_user_id' });

      if (error) {
        console.error('[STORAGE] Bulk connection sync failed:', error);
        logDualWriteError('connection_data', 'bulk_sync', error.message);
      } else {
        console.log('[STORAGE] Synced', rows.length, 'connections to Supabase');
      }
    }

    // Also sync profile + preferences
    const localProfile = includeProfile ? await getProfileData() : null;
    if (localProfile) {
      await syncProfileToSupabase(localProfile);
    }

    // Clear pending flag on success
    localStorage.removeItem('pending_initial_sync');
    console.log('[STORAGE] syncAllConnectionsToSupabase complete');
  } catch (error) {
    console.error('[STORAGE] syncAllConnectionsToSupabase failed:', error);
    logDualWriteError('connection_data', 'bulk_sync', error.message);
  }
};

// ============================================
// ACCOUNT RESET (for dev testing)
// ============================================

/**
 * Full account reset — wipes Supabase + localStorage in one atomic operation
 * Prevents orphaned data that causes messages to render on wrong side
 * @param {string} profileUserId - The profile user ID to reset
 */
/**
 * Delete all server-side data for a user account.
 * THIS IS THE SINGLE SOURCE OF TRUTH FOR ACCOUNT DELETION POLICY.
 * Both fullAccountReset() and dashboard delete call this — no inline implementations.
 *
 * Policy:
 *   When User B deletes their account, User A keeps everything User B shared:
 *   photo, name, contacts, chats, and connection metadata (date, time, location).
 *   Shared data belongs to the recipient — once shared, it's shared.
 *
 * Behavior by branch:
 *   Main: delete User B's own data only (profile, connection_data, sessions, analytics).
 *         Preserve connections + chat_messages so User A's view is unaffected.
 *   Dev branches: full nuke (including connections + chat_messages) for clean testing.
 *
 * What gets deleted (always):
 *   - connection_data (this user's view of connections)
 *   - active_sessions, device_sessions
 *   - connection_timings
 *   - pending_profiles (by phone)
 *   - analytics_events (by identity columns)
 *   - profiles row
 *
 * What gets deleted (dev branches only — shared data):
 *   - chat_messages for all connections involving this user
 *   - connections rows where this user is host or guest
 *   - profile photo from storage
 */
export const deleteUserServerData = async (profileUserId) => {
  // 0. Get profile phone before deleting — needed for pending_profiles cleanup
  const { data: profileRow } = await supabase
    .from('profiles')
    .select('phone')
    .eq('profile_user_id', profileUserId)
    .maybeSingle();
  const profilePhone = profileRow?.phone;

  // 1. Find all connections where this user is host or guest
  const { data: userConns, error: connError } = await supabase
    .from('connections')
    .select('chat_room_id')
    .or(`host_user_id.eq.${profileUserId},guest_user_id.eq.${profileUserId}`);

  if (connError) {
    console.error('[STORAGE] Error querying connections for delete:', connError);
    throw connError;
  }

  const chatRoomIds = (userConns || []).map((c) => c.chat_room_id);
  console.log('[STORAGE] Found', chatRoomIds.length, 'connections for', profileUserId);

  // On main, preserve shared data — the other user keeps what was shared with them.
  // On dev branches, nuke everything for a clean test DB.
  const isMainBranch = process.env.REACT_APP_BRANCH === 'main';

  // 2. Delete this user's own data (always)
  await supabase.from('connection_data').delete().eq('profile_user_id', profileUserId);
  await supabase.from('active_sessions').delete().eq('profile_user_id', profileUserId);
  await supabase.from('device_sessions').delete().eq('profile_user_id', profileUserId);
  await supabase.from('presence_heartbeats').delete().eq('sender_id', profileUserId);

  if (chatRoomIds.length > 0) {
    await supabase.from('connection_timings').delete().in('chat_room_id', chatRoomIds);
    // Dev branches only: nuke shared data (connections + messages + all analytics for these connections)
    if (!isMainBranch) {
      await supabase.from('chat_messages').delete().in('chat_room_id', chatRoomIds);
      await supabase.from('presence_heartbeats').delete().in('chat_room_id', chatRoomIds);
      await supabase.from('connections').delete().in('chat_room_id', chatRoomIds);
    }
  }

  // 3. Delete pending records for this phone
  if (profilePhone) {
    await supabase.from('pending_profiles').delete().eq('phone', profilePhone);
  }

  // 4. Delete analytics data
  await supabase.from('analytics_events').delete().eq('profile_user_id', profileUserId);
  // …and the both-tap offers either side (function-only table, 055 step 12.10).
  // New experience only: the function isn't on prod until the flip, and the
  // old screens make no new server calls.
  if (isNewExperience()) {
    await supabase.rpc('exchange_offers_forget', { p_me: profileUserId });
  }

  // 5. Delete profile row
  await supabase.from('profiles').delete().eq('profile_user_id', profileUserId);

  // 6. Photo: shared data — preserve on main so the other user still sees it.
  // Dev branches only: delete photo from storage for clean testing.
  if (!isMainBranch) {
    try {
      await supabase.storage.from('profile-photos').remove([`${profileUserId}.jpg`]);
    } catch {
      // Photo may not exist
    }
  }

  console.log('[STORAGE] deleteUserServerData complete for:', profileUserId);
};

/**
 * Full account reset — deletes server data + clears local state.
 * Used when a user resets their OWN account (reset button, /reset URL).
 */
export const fullAccountReset = async (profileUserId) => {
  if (!profileUserId) {
    console.error('[STORAGE] fullAccountReset: no profileUserId provided');
    return false;
  }

  try {
    console.log('[STORAGE] fullAccountReset starting for:', profileUserId);

    // Server-side cleanup (shared policy)
    await deleteUserServerData(profileUserId);

    // Client-side cleanup (only when resetting your own account)
    try {
      const { signOut } = await import('../auth/otpAuth');
      await signOut();
    } catch {
      // otpAuth may not be loaded yet, that's fine
    }
    await clearAllData();

    console.log('[STORAGE] fullAccountReset complete for:', profileUserId);
    return true;
  } catch (error) {
    console.error('[STORAGE] fullAccountReset failed:', error);
    return false;
  }
};

// One-time cleanup of legacy keys on module import
try {
  // Clear all legacy profile keys
  localStorage.removeItem('royale_profile');
  localStorage.removeItem('profileName');
  localStorage.removeItem('profilePhotoUrl');
  localStorage.removeItem('profileUserPhone');
  localStorage.removeItem('profileUserEmail');
  localStorage.removeItem('profileUserInstagram');

  // Clear all legacy anon user keys
  localStorage.removeItem('anonUserName');
  localStorage.removeItem('anonUserPhone');
  localStorage.removeItem('anonUserEmail');
  localStorage.removeItem('anonUserInstagram');
  localStorage.removeItem('anonUserPhotoUrl');

  // Clear legacy location permission keys
  localStorage.removeItem('locationPermissionGranted');
  localStorage.removeItem('royale_location_permission');

  // Migrate legacy navigation keys
  const currentScreen = localStorage.getItem('currentScreen');
  if (currentScreen) {
    localStorage.setItem(CURRENT_SCREEN_KEY, currentScreen);
    localStorage.removeItem('currentScreen');
  }

  const selectedContactType = localStorage.getItem('selectedContactType');
  if (selectedContactType) {
    localStorage.setItem(SELECTED_CONTACT_TYPE_KEY, selectedContactType);
    localStorage.removeItem('selectedContactType');
  }

  console.log('Legacy storage keys removed or migrated');
} catch (error) {
  console.error('Error removing legacy storage keys:', error);
}
